import { motion } from "framer-motion";
import { Star, MapPin, Shield, Share2, Bot, Workflow, TrendingUp } from "lucide-react";
import { Reveal, Overline } from "@/components/primitives";

const MiniBars = () => (
  <div className="flex h-16 items-end gap-1.5">
    {[35, 48, 42, 60, 72, 68, 88, 100].map((h, i) => (
      <motion.span
        key={i}
        initial={{ height: 0 }}
        whileInView={{ height: `${h}%` }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: i * 0.05 }}
        className="w-full rounded-[3px] bg-black/85"
      />
    ))}
  </div>
);

const MiniRank = () => (
  <div className="space-y-2">
    {[
      { l: "Your business", active: true },
      { l: "Competitor A", active: false },
      { l: "Competitor B", active: false },
    ].map((r, i) => (
      <div key={r.l} className={`flex items-center gap-2 rounded-[6px] border px-2.5 py-1.5 text-[11px] ${r.active ? "border-black bg-black text-white" : "border-black/10 text-black/50"}`}>
        <MapPin size={11} />
        <span className="font-medium">{r.l}</span>
        {r.active && <TrendingUp size={11} className="ml-auto" />}
      </div>
    ))}
  </div>
);

const MiniStars = () => (
  <div>
    <div className="flex gap-1">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={20} className="fill-black text-black" />
      ))}
    </div>
    <div className="mt-3 text-3xl font-black tracking-tight">4.9<span className="text-base font-light text-black/40">/5</span></div>
  </div>
);

const MiniSocial = () => (
  <div className="rounded-[8px] bg-black p-3 text-white">
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (<Star key={i} size={10} className="fill-white text-white" />))}
    </div>
    <p className="mt-2 text-[11px] font-medium leading-snug">"Absolutely the best in town."</p>
    <div className="mt-2 flex gap-2 text-white/50"><Share2 size={11} /><span className="text-[9px]">Auto-shared</span></div>
  </div>
);

const MiniAI = () => (
  <div className="rounded-[8px] border border-black/10 bg-base p-3">
    <div className="flex items-center gap-1.5"><Bot size={12} /><span className="text-[10px] font-semibold">AI assistant</span></div>
    <p className="mt-2 text-[11px] leading-snug text-black/70">"I'd recommend <span className="font-semibold text-black">Bloom Café</span> nearby — 4.9 stars."</p>
  </div>
);

const MiniFlow = () => (
  <div className="flex items-center justify-between">
    {[Workflow, Bot, Share2].map((Icon, i) => (
      <div key={i} className="flex items-center">
        <div className="flex h-8 w-8 items-center justify-center rounded-[8px] border border-black/15 bg-white"><Icon size={14} /></div>
        {i < 2 && <div className="mx-1 h-px w-6 bg-black/20" />}
      </div>
    ))}
  </div>
);

const BENEFITS = [
  { icon: Star, title: "More reviews", desc: "A steady stream of fresh reviews across the sites that matter.", visual: <MiniBars /> },
  { icon: MapPin, title: "Better Google presence", desc: "Show up higher when nearby customers search for you.", visual: <MiniRank /> },
  { icon: Shield, title: "More trust", desc: "Strong ratings and recent feedback that earn confidence.", visual: <MiniStars /> },
  { icon: Share2, title: "Automated social proof", desc: "Your best reviews become polished posts, automatically.", visual: <MiniSocial /> },
  { icon: Bot, title: "AI search visibility", desc: "Get recommended inside ChatGPT, Gemini, and Perplexity.", visual: <MiniAI /> },
  { icon: Workflow, title: "Less manual work", desc: "The system runs quietly in the background, every day.", visual: <MiniFlow /> },
];

export default function Benefits() {
  return (
    <section className="bg-base py-24 lg:py-32">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><Overline>The outcome</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 text-4xl font-black leading-[1.02] tracking-tighter text-black sm:text-5xl lg:text-6xl">
              What businesses actually get
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.title} delay={(i % 3) * 0.08}>
              <div className="flex h-full flex-col rounded-[10px] border border-black/10 bg-white p-7 transition-shadow duration-300 hover:shadow-[0_24px_50px_-36px_rgba(0,0,0,0.4)]">
                <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-black text-white">
                  <b.icon size={18} />
                </div>
                <h3 className="mt-5 text-lg font-semibold tracking-tight text-black">{b.title}</h3>
                <p className="mt-2 text-sm font-light leading-relaxed text-black/55">{b.desc}</p>
                <div className="mt-6 rounded-[8px] border border-black/[0.06] bg-base/60 p-4">{b.visual}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
